import React, { useEffect, useState } from "react";
import { ScrollText, ShieldCheck, AlertTriangle, Sparkles, Tag } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner, StatCard } from "@/components/Bits";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const KIND_LABEL = { character: "Character", game: "World", asset: "Asset" };
const ORIGIN_LABEL = { ai: "AI-generated", original: "My original", uploaded: "Uploaded", licensed: "Licensed" };
const ORIGIN_COLOR = { ai: "#3b9eff", original: "#22c55e", uploaded: "#f59e0b", licensed: "#a855f7" };

export default function Rights() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try { const { data } = await api.get("/rights"); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const update = async (row, patch) => {
    try {
      await api.patch(`/rights/${row.kind}/${row.id}`, patch);
      setData((d) => ({
        ...d,
        items: d.items.map((it) => (it.id === row.id && it.kind === row.kind ? { ...it, ...patch } : it)),
      }));
      toast.success("Rights updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <Spinner />;
  const { items, summary } = data;
  const licenses = summary.licenses || {};
  const origins = summary.origins || [];
  const rows = filter === "all" ? items : items.filter((i) => i.origin === filter);

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="rights-page">
      <PageHeader title="Ownership & Rights" icon={ScrollText}
        subtitle="A clear register of everything you've created — its origin, who owns it, and how it's licensed." />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total creations" value={summary.total} icon={Sparkles} accent="#3b9eff" />
        <StatCard label="Listed for sale" value={summary.listed} icon={Tag} accent="#22c55e" delay={1} />
        <StatCard label="AI-generated" value={summary.by_origin?.ai || 0} icon={ShieldCheck} accent="#3b9eff" delay={2} />
        <StatCard label="Protected-name flags" value={summary.protected} icon={AlertTriangle} accent="#ef4444" delay={3} />
      </div>

      <div className="rounded-2xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-4 mb-5 text-sm text-[hsl(var(--muted-foreground))]">
        <ShieldCheck className="w-4 h-4 inline text-[hsl(var(--primary))] mr-1.5" />
        Everything generated in GameView is <b className="text-[hsl(var(--foreground))]">original AI work or your own uploads</b> — you keep the rights. Set a license per item to control how it can be reused or sold.
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-[hsl(var(--muted-foreground))]">Filter by origin:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="rights-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All origins</SelectItem>
            {origins.map((o) => <SelectItem key={o} value={o}>{ORIGIN_LABEL[o] || o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-[hsl(var(--muted-foreground))] py-10 text-center">No creations in this view yet.</div>
      ) : (
        <div className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden" data-testid="rights-table">
          <div className="hidden sm:grid grid-cols-[2fr_1fr_1.3fr_1.6fr] gap-3 px-4 py-2.5 bg-[hsl(var(--muted))]/40 text-xs font-semibold text-[hsl(var(--muted-foreground))]">
            <span>Creation</span><span>Type</span><span>Origin</span><span>License</span>
          </div>
          {rows.map((row) => (
            <div key={`${row.kind}-${row.id}`} data-testid={`rights-row-${row.id}`}
              className="grid sm:grid-cols-[2fr_1fr_1.3fr_1.6fr] gap-3 px-4 py-3 border-t border-[hsl(var(--border))] items-center">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xl shrink-0">{row.emoji || "📦"}</span>
                <div className="min-w-0">
                  <div className="font-semibold truncate flex items-center gap-1.5">
                    {row.name}
                    {row.protected && <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" title={`Resembles protected IP: ${row.protected}`} />}
                    {row.listed && <Badge variant="secondary" className="text-[9px]">Listed</Badge>}
                  </div>
                  {row.protected && <div className="text-[10px] text-red-500">Resembles “{row.protected}” — rename before selling.</div>}
                </div>
              </div>
              <div><Badge variant="outline" className="text-[10px]">{KIND_LABEL[row.kind]}</Badge></div>
              <div>
                <Select value={row.origin} onValueChange={(v) => update(row, { origin: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`origin-${row.id}`}>
                    <span className="w-2 h-2 rounded-full mr-1.5 inline-block" style={{ background: ORIGIN_COLOR[row.origin] }} />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {origins.map((o) => <SelectItem key={o} value={o}>{ORIGIN_LABEL[o] || o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={row.license} onValueChange={(v) => update(row, { license: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`license-${row.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(licenses).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
